import { useEffect, useRef, useState } from "react";
import { TextDisplay } from "./TextDisplay";
import Toggle from "../Toggle/Toggle";
import { Spinner, SpinnerAnimation } from "../Spinner/Spinner";
import { classNames } from "../../style/common";

export type TextEditorProps = {
    value: string,
    canEdit: boolean,
    commitDuration?: number,
    onChange?: (value: string) => void,
    onCommit?: (value: string) => void,
    classNames?: {
        input?: string | undefined
    }
}

export function TextEditor(props: TextEditorProps) {
    const [isPreview, setIsPreview] = useState(false);
    const [unsavedText, setUnsavedText] = useState(props.value);
    const [pendingSave, setPendingSave] = useState(false);
    const [commitDuration, setCommitDuration] = useState(props.commitDuration ?? 1000);
    const commitRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (pendingSave) {
            return;
        }

        setUnsavedText(props.value);
    }, [props.value, pendingSave]);

    useEffect(() => {
        if (props.canEdit) {
            setIsPreview(false);
            return;
        }

        setIsPreview(true);
    }, [props.canEdit]);

    useEffect(() => {
        setCommitDuration(props.commitDuration ?? 1000);
    }, [props.commitDuration]);

    return (
        <>
            {
                props.canEdit &&
                <div className="flex gap-3 mb-4">
                    <Toggle
                        label="Preview"
                        disabled={false}
                        isEnabled={isPreview}
                        onToggleChange={(isEnabled) => setIsPreview(isEnabled)} />

                    {
                        pendingSave &&
                        <SpinnerAnimation className="!h-6 !w-6" />
                    }
                </div>
            }
            {
                isPreview
                    ? <TextDisplay
                        value={unsavedText} />
                    : <textarea
                        maxLength={4000}
                        onChange={(event) => {
                            if (event.target.value.length > 4000) {
                                return;
                            }

                            setUnsavedText(event.target.value);

                            if (commitRef?.current) {
                                clearTimeout(commitRef.current);
                            }

                            if (props.onCommit) {
                                setPendingSave(true);
                            }

                            commitRef.current = setTimeout(() => {
                                if (props.onCommit) {
                                    props.onCommit(event.target.value);
                                    setPendingSave(false);
                                }
                            }, commitDuration);

                            if (props.onChange) {
                                props.onChange(event.target.value);
                            }
                        }}
                        className={classNames("w-full bg-primary border border-color-border rounded h-80", props.classNames?.input)}
                        value={unsavedText} />
            }
        </>
    )
}